import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { useRoleStore } from '@/store/role.store'
import { authService } from '@/services/auth.service'
import type { UserRole } from '@/types/user.types'
import AppShell from '@/components/layout/AppShell'
import PublicLayout from '@/components/layout/PublicLayout'
import { FeatureGate } from '@/components/FeatureGate'

const LandingPage = lazy(() => import('@/pages/marketing/LandingPage'))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))

const DiscoverPage = lazy(() => import('@/pages/attendee/DiscoverPage'))
const EventDetailPage = lazy(() => import('@/pages/attendee/EventDetailPage'))
const CheckoutPage = lazy(() => import('@/pages/attendee/CheckoutPage'))
const TicketPage = lazy(() => import('@/pages/attendee/TicketPage'))
const TicketDetailPage = lazy(() => import('@/pages/attendee/TicketDetailPage'))
const WalletPage = lazy(() => import('@/pages/attendee/WalletPage'))
const ProfilePage = lazy(() => import('@/pages/attendee/ProfilePage'))
const VendorScanPage = lazy(() => import('@/pages/attendee/VendorScanPage'))

const PublicTicketPage = lazy(() => import('@/pages/public/PublicTicketPage'))
const CheckoutCallbackPage = lazy(() => import('@/pages/public/CheckoutCallbackPage'))

const OrgDashboardPage = lazy(() => import('@/pages/organiser/DashboardPage'))
const ManageEventPage = lazy(() => import('@/pages/organiser/ManageEventPage'))
const CreateEventPage = lazy(() => import('@/pages/organiser/CreateEventPage'))
const LiveManagementPage = lazy(() => import('@/pages/organiser/LiveManagementPage'))
const TicketTiersPage = lazy(() => import('@/pages/organiser/TicketTiersPage'))
const OrgAnalyticsPage = lazy(() => import('@/pages/organiser/AnalyticsPage'))
const RevenuePage = lazy(() => import('@/pages/organiser/RevenuePage'))
const OrgVerificationPage = lazy(() => import('@/pages/organiser/VerificationPage'))

const VendorDashboardPage = lazy(() => import('@/pages/vendor/VendorDashboardPage'))
const MenuBuilderPage = lazy(() => import('@/pages/vendor/MenuBuilderPage'))
const LiveOrdersPage = lazy(() => import('@/pages/vendor/LiveOrdersPage'))
const EarningsPage = lazy(() => import('@/pages/vendor/EarningsPage'))
const ContactsPage = lazy(() => import('@/pages/vendor/ContactsPage'))
const VendorQRPage = lazy(() => import('@/pages/vendor/VendorQRPage'))
const VendorVerificationPage = lazy(() => import('@/pages/vendor/VerificationPage'))

const AdminDashboardPage = lazy(() => import('@/pages/admin/DashboardPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const AdminUserDetailPage = lazy(() => import('@/pages/admin/UserDetailPage'))
const AdminEventsPage = lazy(() => import('@/pages/admin/EventsPage'))
const AdminKYCPage = lazy(() => import('@/pages/admin/KYCPage'))
const AdminDisputesPage = lazy(() => import('@/pages/admin/DisputesPage'))
const AdminRevenuePage = lazy(() => import('@/pages/admin/RevenuePage'))
const AdminAuditLogPage = lazy(() => import('@/pages/admin/AuditLogPage'))
const AdminManagementPage = lazy(() => import('@/pages/admin/AdminManagementPage'))
const AdminFeeConfigPage = lazy(() => import('@/pages/admin/FeeConfigPage'))
const AdminCPRPoolPage = lazy(() => import('@/pages/admin/CPRPoolPage'))
const AdminPayoutsPage = lazy(() => import('@/pages/admin/PayoutsPage'))

function homePathForRole(role: UserRole) {
  if (role === 'admin') return '/admin'
  if (role === 'organiser') return '/organiser'
  if (role === 'vendor') return '/vendor'
  // attendees go to the events feed; `/` is the public marketing landing page.
  return '/events'
}

function defaultPathForRoles(roles: UserRole[]) {
  return homePathForRole(roles[0] ?? 'attendee')
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isBootstrapping } = useAuthStore()
  if (isBootstrapping) return <PageLoader />
  if (!isAuthenticated) return <Navigate to="/" replace />
  return <>{children}</>
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isBootstrapping } = useAuthStore()
  if (isBootstrapping) return <PageLoader />
  if (isAuthenticated && user) {
    return <Navigate to={defaultPathForRoles(user.roles)} replace />
  }
  return <>{children}</>
}

// Public landing page at `/`. Logged-in users skip the marketing page and
// jump straight to their role's home.
function HomeRoute() {
  const { user, isAuthenticated, isBootstrapping } = useAuthStore()
  if (isBootstrapping) return <PageLoader />
  if (isAuthenticated && user) {
    return <Navigate to={defaultPathForRoles(user.roles)} replace />
  }
  return <LandingPage />
}

function RoleGuard({ role, children }: { role: UserRole; children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user)
  const setRole = useRoleStore((state) => state.setRole)

  useEffect(() => {
    if (user?.roles?.includes(role)) {
      setRole(role)
    }
  }, [role, setRole, user])

  if (!user) return <Navigate to="/" replace />
  if (!user.roles.includes(role)) {
    return <Navigate to={defaultPathForRoles(user.roles)} replace />
  }
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-cream">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-gold)] border-t-transparent" />
    </div>
  )
}

export default function App() {
  const { activeRole, syncRoles } = useRoleStore()
  const { setUser, logout, setBootstrapping } = useAuthStore()

  useEffect(() => {
    let cancelled = false

    async function bootstrapSession() {
      try {
        const user = await authService.getProfile()
        if (cancelled) return
        setUser(user)
        syncRoles(user.roles)
      } catch {
        if (cancelled) return
        logout()
      } finally {
        if (!cancelled) {
          setBootstrapping(false)
        }
      }
    }

    void bootstrapSession()

    return () => {
      cancelled = true
    }
  }, [logout, setBootstrapping, setUser, syncRoles])

  return (
    <div data-role={activeRole}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnlyRoute>
                <RegisterPage />
              </PublicOnlyRoute>
            }
          />

          {/* Public surfaces — marketing landing + event browsing.
              Logged-in users on `/` get redirected by HomeRoute to their
              role home; logged-in users on /events or /events/:id stay put
              (the PublicLayout's nav swaps to "Open dashboard"). */}
          <Route path="/" element={<HomeRoute />} />
          <Route element={<PublicLayout />}>
            <Route path="/events" element={<DiscoverPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
          </Route>

          {/* WS0.1(a) slice 2 — public, login-free ticket viewer.
              Outside PublicLayout intentionally: the page is its own
              standalone surface (no marketing nav chrome), used by the
              email link and the 'resend my ticket' flow for guests. */}
          <Route path="/ticket/:token" element={<PublicTicketPage />} />

          {/* WS1 Slice C Phase 2 — direct checkout is GUEST-capable (§1.6),
              so it lives outside ProtectedRoute; logged-in buyers get their
              details prefilled. /checkout/callback is where the payment
              partner redirects back to (CHECKOUT_CALLBACK_URL). */}
          <Route path="/events/:id/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/callback" element={<CheckoutCallbackPage />} />

          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/tickets" element={<TicketPage />} />
            <Route path="/tickets/:id" element={<TicketPage />} />
            <Route path="/tickets/:id/view" element={<TicketDetailPage />} />
            <Route path="/wallet" element={
              <FeatureGate flag="ATTENDEE_WALLET" redirectTo="/events">
                <WalletPage />
              </FeatureGate>
            } />
            <Route path="/scan" element={
              <FeatureGate flag="VENDOR_PAYMENTS" redirectTo="/events">
                <VendorScanPage />
              </FeatureGate>
            } />
            <Route path="/profile" element={<ProfilePage />} />

            <Route path="/organiser" element={<RoleGuard role="organiser"><OrgDashboardPage /></RoleGuard>} />
            <Route path="/organiser/events/create" element={<RoleGuard role="organiser"><CreateEventPage /></RoleGuard>} />
            <Route path="/organiser/events/:id" element={<RoleGuard role="organiser"><ManageEventPage /></RoleGuard>} />
            <Route path="/organiser/events/:id/live" element={<RoleGuard role="organiser"><LiveManagementPage /></RoleGuard>} />
            <Route path="/organiser/events/:id/tickets" element={<RoleGuard role="organiser"><TicketTiersPage /></RoleGuard>} />
            <Route path="/organiser/events/:id/analytics" element={<RoleGuard role="organiser"><OrgAnalyticsPage /></RoleGuard>} />
            <Route path="/organiser/revenue" element={<RoleGuard role="organiser"><RevenuePage /></RoleGuard>} />
            <Route path="/organiser/verification" element={<RoleGuard role="organiser"><OrgVerificationPage /></RoleGuard>} />

            <Route path="/vendor" element={<RoleGuard role="vendor"><VendorDashboardPage /></RoleGuard>} />
            <Route path="/vendor/menu" element={<RoleGuard role="vendor"><MenuBuilderPage /></RoleGuard>} />
            <Route path="/vendor/orders/live" element={
              <RoleGuard role="vendor">
                <FeatureGate flag="VENDOR_ORDER_BOARD" redirectTo="/vendor">
                  <LiveOrdersPage />
                </FeatureGate>
              </RoleGuard>
            } />
            <Route path="/vendor/earnings" element={
              <RoleGuard role="vendor">
                <FeatureGate flag="VENDOR_EARNINGS_DASHBOARD" redirectTo="/vendor">
                  <EarningsPage />
                </FeatureGate>
              </RoleGuard>
            } />
            <Route path="/vendor/contacts" element={<RoleGuard role="vendor"><ContactsPage /></RoleGuard>} />
            <Route path="/vendor/events/:eventId/qr" element={
              <RoleGuard role="vendor">
                <FeatureGate flag="VENDOR_PAYMENTS" redirectTo="/vendor">
                  <VendorQRPage />
                </FeatureGate>
              </RoleGuard>
            } />
            <Route path="/vendor/verification" element={<RoleGuard role="vendor"><VendorVerificationPage /></RoleGuard>} />

            <Route path="/admin" element={<RoleGuard role="admin"><AdminDashboardPage /></RoleGuard>} />
            <Route path="/admin/users" element={<RoleGuard role="admin"><AdminUsersPage /></RoleGuard>} />
            <Route path="/admin/users/:id" element={<RoleGuard role="admin"><AdminUserDetailPage /></RoleGuard>} />
            <Route path="/admin/events" element={<RoleGuard role="admin"><AdminEventsPage /></RoleGuard>} />
            <Route path="/admin/kyc" element={<RoleGuard role="admin"><AdminKYCPage /></RoleGuard>} />
            <Route path="/admin/disputes" element={<RoleGuard role="admin"><AdminDisputesPage /></RoleGuard>} />
            <Route path="/admin/revenue" element={<RoleGuard role="admin"><AdminRevenuePage /></RoleGuard>} />
            <Route path="/admin/audit" element={<RoleGuard role="admin"><AdminAuditLogPage /></RoleGuard>} />
            <Route path="/admin/admins" element={<RoleGuard role="admin"><AdminManagementPage /></RoleGuard>} />
            <Route path="/admin/fees" element={<RoleGuard role="admin"><AdminFeeConfigPage /></RoleGuard>} />
            <Route path="/admin/cpr" element={<RoleGuard role="admin"><AdminCPRPoolPage /></RoleGuard>} />
            <Route path="/admin/payouts" element={<RoleGuard role="admin"><AdminPayoutsPage /></RoleGuard>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}
